<template>
  <!-- Flat, sortable, paged order-lines table. Presentational — the parent owns the data,
       sort/page state, and reloading; this just renders + emits sort/page/page-size.
       fill=true (in a dashboard grid card) makes it a full-height flex column whose table
       body scrolls; fill=false (Layout 2 standalone) caps the body at 70vh. -->
  <div class="olt" :class="{ 'olt-fill': fill }">
    <div class="olt-meta">
      <span v-if="loading">Loading…</span>
      <span v-else>{{ fmtNum(totalRows) }} line{{ totalRows === 1 ? '' : 's' }} · page {{ page }} of {{ pageCount }}</span>
      <span class="olt-spacer"></span>
      <label class="olt-pagesize">Rows
        <select :value="pageSize" @change="$emit('page-size', Number($event.target.value))">
          <option :value="25">25</option><option :value="50">50</option><option :value="100">100</option><option :value="200">200</option>
        </select>
      </label>
    </div>
    <div class="olt-tablewrap">
      <table class="olt-table">
        <thead>
          <tr>
            <th v-for="c in columns" :key="c.key || c.label" :class="{ 'is-sortable': c.sortable, 'is-num': c.num }" @click="c.sortable && $emit('sort', c.key)">
              {{ c.label }}<span v-if="c.sortable && sort === c.key" class="olt-arrow">{{ dir === 'asc' ? '▲' : '▼' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.id">
            <td>{{ r.orderNo }}</td>
            <td>{{ fmtDate(r.orderDate) }}</td>
            <td><span class="olt-cust">{{ r.customerName }}</span><span class="olt-custid">{{ r.customerId }}</span></td>
            <td>{{ r.partNumber }}</td>
            <td class="olt-desc">{{ r.partDescription }}</td>
            <td class="is-num">{{ fmtNum(r.qty) }}</td>
            <td class="is-num">{{ fmtMoney(r.unitPrice) }}</td>
            <td class="is-num">{{ fmtMoney(r.lineTotal) }}</td>
            <td><span class="olt-pill" :class="r.isInvoiced ? 'is-inv' : 'is-open'">{{ r.isInvoiced ? 'Invoiced' : 'Open' }}</span></td>
            <td>{{ r.salesRep }}</td>
            <td>{{ r.territory }}</td>
          </tr>
          <tr v-if="!loading && !rows.length"><td :colspan="columns.length" class="olt-empty">No orders match these filters.</td></tr>
        </tbody>
      </table>
    </div>
    <div class="olt-pager" v-if="pageCount > 1">
      <button class="olt-btn" :disabled="page <= 1" @click="$emit('page', 1)">« First</button>
      <button class="olt-btn" :disabled="page <= 1" @click="$emit('page', page - 1)">‹ Prev</button>
      <span class="olt-pagenum">Page {{ page }} / {{ pageCount }}</span>
      <button class="olt-btn" :disabled="page >= pageCount" @click="$emit('page', page + 1)">Next ›</button>
      <button class="olt-btn" :disabled="page >= pageCount" @click="$emit('page', pageCount)">Last »</button>
    </div>
  </div>
</template>

<script>
export default {
  name: "OrderLinesTable",
  props: {
    columns: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    totalRows: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    pageCount: { type: Number, default: 1 },
    pageSize: { type: Number, default: 50 },
    sort: { type: String, default: "" },
    dir: { type: String, default: "desc" },
    fill: { type: Boolean, default: false },
  },
  methods: {
    fmtMoney(n) { return (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" }); },
    fmtNum(n) { return (Number(n) || 0).toLocaleString("en-US"); },
    fmtDate(d) { return d ? String(d).slice(0, 10) : ""; },
  },
};
</script>

<style scoped>
.olt-fill { height: 100%; display: flex; flex-direction: column; }
.olt-meta { display: flex; align-items: center; gap: 10px; font-size: 12px; color: #666; padding: 4px 4px 8px; flex: none; }
.olt-spacer { flex: 1; }
.olt-pagesize select { margin-left: 6px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 2px 6px; font-size: 12px; }
.olt-tablewrap { overflow: auto; border: 1px solid #e6e6e6; border-radius: 8px; }
.olt-fill .olt-tablewrap { flex: 1; min-height: 0; }
.olt:not(.olt-fill) .olt-tablewrap { max-height: 70vh; }
.olt-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.olt-table thead th { position: sticky; top: 0; z-index: 2; background: #9e2436; text-align: left; padding: 8px 10px; border-bottom: 1px solid #7e1c2b; white-space: nowrap; font-weight: 700; color: #fff; text-transform: uppercase; font-size: 11px; letter-spacing: .02em; }
.olt-table th.is-sortable { cursor: pointer; user-select: none; }
.olt-table th.is-sortable:hover { color: #ffd9df; }
.olt-table th.is-num, .olt-table td.is-num { text-align: right; }
.olt-arrow { margin-left: 4px; font-size: 10px; color: #ffd9df; }
.olt-table tbody td { padding: 7px 10px; border-bottom: 1px solid #f0f0f0; vertical-align: top; }
.olt-table tbody tr:hover { background: #fafafa; }
.olt-cust { display: block; font-weight: 600; color: #333; }
.olt-custid { display: block; font-size: 10.5px; color: #999; }
.olt-desc { color: #555; max-width: 240px; }
.olt-pill { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 10px; white-space: nowrap; }
.olt-pill.is-inv { background: #e7f5e8; color: #1f6e23; border: 1px solid #b6e0b9; }
.olt-pill.is-open { background: #fff4e0; color: #8a6d1a; border: 1px solid #f0c36d; }
.olt-empty { text-align: center; color: #999; padding: 28px; }
.olt-pager { display: flex; align-items: center; gap: 8px; justify-content: center; margin-top: 12px; flex: none; }
.olt-pagenum { font-size: 12px; color: #666; }
.olt-btn { border: 1px solid #d4d4d8; background: #fff; color: #444; border-radius: 6px; padding: 4px 10px; font-size: 12px; cursor: pointer; }
.olt-btn:disabled { opacity: .5; cursor: default; }
.olt-btn:not(:disabled):hover { background: #f3f3f5; }
</style>
